import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

interface InternPanelProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  actions?: React.ReactNode;
  icon?: React.ReactNode;
  collapsible?: boolean;
  variant?: "default" | "outlined";
}

const InternPanel = ({
  title,
  subtitle,
  children,
  defaultExpanded = true,
  actions,
  icon,
  collapsible = true,
  variant = "default",
}: InternPanelProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = () => {
    if (collapsible) {
      setExpanded(!expanded);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor:
          variant === "outlined" ? "transparent" : "var(--intern-bg-primary)",
        border:
          variant === "outlined"
            ? "1px dashed var(--intern-border-default)"
            : "1px solid var(--intern-border-default)",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        onClick={collapsible ? handleToggle : undefined}
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1, sm: 1.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: collapsible ? "pointer" : "default",
          userSelect: "none",
          bgcolor: expanded ? "transparent" : "var(--intern-bg-secondary)",
          borderBottom: expanded
            ? "1px solid var(--intern-border-default)"
            : "none",
          transition: "background-color 0.15s ease",
          "&:hover": collapsible
            ? {
                bgcolor: "var(--intern-bg-hover)",
              }
            : {},
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: 1, sm: 1.5 }}
          sx={{ minWidth: 0, flex: 1 }}
        >
          {icon && (
            <Box
              sx={{
                color: "var(--intern-text-tertiary)",
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                "& svg": { fontSize: { xs: 18, sm: 20 } },
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                color: "var(--intern-text-primary)",
                lineHeight: 1.3,
                fontSize: { xs: "0.8125rem", sm: "0.875rem" },
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: "var(--intern-text-tertiary)",
                  fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                  display: { xs: "none", sm: "block" },
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ flexShrink: 0 }}
        >
          {actions && !expanded && (
            <Box onClick={(e) => e.stopPropagation()}>{actions}</Box>
          )}
          {collapsible && (
            <IconButton
              size="small"
              sx={{
                color: "var(--intern-text-tertiary)",
                p: { xs: 0.5, sm: 1 },
                "&:hover": {
                  color: "var(--intern-text-primary)",
                },
              }}
            >
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              )}
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>{children}</Box>
        {/* Footer Actions */}
        {actions && expanded && (
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1, sm: 1.5 },
              borderTop: "1px solid var(--intern-border-default)",
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {actions}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default InternPanel;
